import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';

type DomainState = {
  domain: string | null;
  domainId: string | null;
  status: string | null;
  verifiedAt: string | null;
  fromAddress: string | null;
  records:
    | Array<{
        record: string;
        name: string;
        type: string;
        ttl: string;
        status: string;
        value: string;
      }>
    | null;
};

/**
 * Per-firm sending domain. Three-step flow:
 *   1. POST /api/firm/email-domain { domain }  → returns DNS records
 *   2. Firm publishes records on their DNS, comes back here, hits Verify
 *   3. PUT /api/firm/email-from { from }       → starts sending as the firm
 */
export function EmailDomainModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['email', 'common']);
  const [state, setState] = useState<DomainState | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      const d = await apiFetch<DomainState>('/api/firm/email-domain');
      setState(d);
      setFromInput(d.fromAddress ?? '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createDomain() {
    if (!domainInput.trim()) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await apiFetch('/api/firm/email-domain', {
        method: 'POST',
        body: JSON.stringify({ domain: domainInput.trim() }),
      });
      setDomainInput('');
      setMsg(t('domain.created'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function verifyDomain() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await apiFetch('/api/firm/email-domain/verify', { method: 'POST' });
      setMsg(t('domain.verify_triggered'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeDomain() {
    if (!confirm(t('domain.remove_confirm'))) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await apiFetch('/api/firm/email-domain', { method: 'DELETE' });
      setMsg(t('domain.removed'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveFrom() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await apiFetch('/api/firm/email-from', {
        method: 'PUT',
        body: JSON.stringify({ from: fromInput.trim() }),
      });
      setMsg(t('domain.from_saved'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const isVerified = state?.status === 'verified';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{t('domain.title')}</div>
            <div className="form-preview-type">{t('domain.subtitle')}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        {!state?.domain ? (
          <div className="form-section">
            <div className="section-label">{t('domain.add_section')}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
              {t('domain.add_hint')}
            </div>
            <div className="field">
              <label>{t('domain.input_label')}</label>
              <input
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="mail.firm.co.il"
              />
            </div>
            <div className="form-actions" style={{ marginTop: 10 }}>
              <button className="btn-primary" onClick={createDomain} disabled={busy || !domainInput.trim()}>
                {t('domain.add_submit')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="form-section">
              <div className="section-label">{t('domain.status_section')}</div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <strong>{state.domain}</strong> ·{' '}
                <span
                  className={`badge ${isVerified ? 'b-open' : 'b-pending'}`}
                  style={{ marginInlineStart: 6 }}
                >
                  {t(`domain.statuses.${state.status ?? 'pending'}`, { defaultValue: state.status })}
                </span>
              </div>
              {!isVerified && state.records && state.records.length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--text2)', margin: '8px 0' }}>
                    {t('domain.records_hint')}
                  </div>
                  <div
                    style={{
                      background: 'var(--surface2)',
                      borderRadius: 6,
                      padding: 10,
                      fontSize: 11,
                      fontFamily: 'monospace',
                      overflow: 'auto',
                    }}
                  >
                    {state.records.map((r, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <strong>{r.type}</strong> {r.name}
                        <br />
                        <span style={{ color: 'var(--text2)' }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="form-actions" style={{ marginTop: 10 }}>
                {!isVerified && (
                  <button className="btn-primary" onClick={verifyDomain} disabled={busy}>
                    {t('domain.verify')}
                  </button>
                )}
                <button className="btn-cancel" onClick={removeDomain} disabled={busy}>
                  {t('domain.remove')}
                </button>
              </div>
            </div>

            {isVerified && (
              <div className="form-section">
                <div className="section-label">{t('domain.from_section')}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
                  {t('domain.from_hint', { domain: state.domain })}
                </div>
                <div className="field">
                  <label>{t('domain.from_label')}</label>
                  <input
                    value={fromInput}
                    onChange={(e) => setFromInput(e.target.value)}
                    placeholder={`Cohen & Co <hello@${state.domain}>`}
                  />
                </div>
                <div className="form-actions" style={{ marginTop: 10 }}>
                  <button className="btn-primary" onClick={saveFrom} disabled={busy}>
                    {t('domain.from_save')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {msg && (
          <div className="form-section" style={{ color: 'var(--green)' }}>
            {msg}
          </div>
        )}
        {err && (
          <div className="form-section" style={{ color: 'var(--red)' }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
